import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, CircularProgress
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { DriverStatus } from "../../types/enums";
import { Driver, allFields, booleanFields } from "./driverTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  editLoading: boolean;
  selectedDriver: Driver | null;
  editData: Partial<Driver>;
  onFieldChange: (field: keyof Driver, value: string | boolean | DriverStatus) => void;
  onSave: () => void;
  canWrite: boolean;
  onGoToDetails: (id: number) => void;
}

/** Full driver details + inline editable fields (quick view from the list). */
const DriverQuickViewModal: React.FC<Props> = ({
  open, onClose, editLoading, selectedDriver, editData, onFieldChange, onSave, canWrite, onGoToDetails,
}) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableEnforceFocus>
      <DialogTitle>Driver Details</DialogTitle>
      <DialogContent>
        {editLoading ? (
            <CircularProgress />
        ) : selectedDriver && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Visibility />}
                    onClick={() => onGoToDetails(selectedDriver.id)}
                >
                  Go to Full Details Page
                </Button>
              </Box>

              <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                    gap: 2,
                    mt: 1,
                  }}
              >
                <TextField label="ID" value={selectedDriver.id} fullWidth disabled />
                <TextField label="User UID" value={selectedDriver.userUid} fullWidth disabled />
                <TextField label="Created At" value={selectedDriver.createdAt || ""} fullWidth disabled />
                <TextField label="Updated At" value={selectedDriver.updatedAt || ""} fullWidth disabled />
                <TextField label="Enrolled By" value={selectedDriver.enrolledBy ? `${selectedDriver.enrolledBy.firstName} ${selectedDriver.enrolledBy.lastName || ''}`.trim() : "-"} fullWidth disabled />
                <TextField label="Approved By" value={selectedDriver.approvedBy ? `${selectedDriver.approvedBy.firstName} ${selectedDriver.approvedBy.lastName || ''}`.trim() : "-"} fullWidth disabled />
                <TextField
                    key="status"
                    label="Status"
                    value={editData.status ?? DriverStatus.OFFLINE}
                    fullWidth
                    disabled
                />
                {allFields.filter(f => !booleanFields.includes(f as any) && f !== 'status' && !['cImageUrl', 'dlImageUrl', 'profilePhotoUrl', 'mandatoryPermissions', 'insuranceUrls', 'pucUrls', 'statePermitUrls', 'aitpUrls'].includes(f)).map(field => (
                    <TextField
                        key={field}
                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                        value={editData[field as keyof Driver] ?? ""}
                        fullWidth
                        onChange={e => onFieldChange(field as keyof Driver, e.target.value)}
                    />
                ))}
                {booleanFields.map(field => (
                    <TextField
                        select
                        key={field}
                        label={field.charAt(0).toUpperCase() + field.slice(1)}
                        value={editData[field as keyof Driver] ? "yes" : "no"}
                        fullWidth
                        onChange={e =>
                            onFieldChange(
                                field as keyof Driver,
                                e.target.value === "yes"
                            )
                        }
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                ))}
              </Box>
            </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canWrite && (
          <Button
              variant="contained"
              onClick={onSave}
              disabled={editLoading}
          >Save</Button>
        )}
      </DialogActions>
    </Dialog>
);

export default DriverQuickViewModal;
